
import Vue from 'vue';
import Component from 'vue-class-component';
import { validators } from '@/utils/validations';
import moment from 'moment-timezone';
import { MapOptions } from 'vue2-google-maps';
import { CountryModel } from '@/models/country.model';
import { Emit, Ref, Watch } from 'vue-property-decorator';
import MembershipCard from '@/components/Shared/MembershipCard.vue';
import FileUpload from '@/components/Shared/FileUpload.vue';
import PopupProfileSave from '@/components/Popups/PopupProfileSave.vue';
import {
  countriesState,
  countryModule,
  userModule,
  userState
} from '@/utils/store-acessor';
import {
  ProfileContestantModel,
  ProfileModel,
  UserModel,
  UserProfileModel
} from '@/models/user.model';
import GoogleSearch from '@/components/Shared/GoogleSearch.vue';
import { getGooGleMapStyle, GoogleLocationSelected } from '@/utils/map';
import { API_URL } from '@/api/axios.config.api';

@Component({
  components: {
    GoogleSearch,
    MembershipCard,
    FileUpload,
    PopupProfileSave
  }
})
export default class ProfileInfoConfig extends Vue {
  @countryModule.Getter('countries') countries!: CountryModel[];
  @countryModule.Getter('loading') loading!: boolean;
  @userModule.Getter('user') user!: UserModel;
  @userModule.Getter('profile') profile!: UserProfileModel;
  @userModule.Getter('contestant') contestant!: ProfileContestantModel;
  @userModule.Getter('hasProfile') userHasProfile!: boolean;
  @userModule.Getter('timezone') timezone!: string;
  model = new ProfileModel();
  // @ts-ignore
  @Ref() readonly profileForm!: VForm;
  @Ref() readonly fileUpload!: FileUpload;
  genders: { label: string; value: string }[] = [
    { label: 'Male', value: 'm' },
    { label: 'Female', value: 'f' },
    { label: 'Other', value: 'o' }
  ];
  maxDate: string = new Date().toISOString().substr(0, 7);
  readonly rules = validators;
  modal = false;
  valid = false;
  showLoading = false;
  uploadFinish = true;
  progress = 0;
  avatarApiUrl = `${API_URL}v1/contestants/profile/avatar`;

  uploading: boolean = false;
  submitting: boolean = false;
  selectedDate: Date | null = null;

  snackbarVisibility: boolean = false;
  snackbarColor: string | null = null;
  snackbarText: string = '';
  buttonTrigger: boolean = false;

  googleLocation: GoogleLocationSelected | null = null;
  center = { lat: 45.508, lng: -73.587 };
  mapStyle: MapOptions = {
    styles: getGooGleMapStyle,
    mapTypeControl: false,
    clickableIcons: true,
    streetViewControl: false,
    zoomControl: true,
    zoom: 2,
    maxZoom: 15,
    fullscreenControl: true
  };

  async mounted() {
    countriesState.updateLoading(true);
    await userState.fetchUser();
    await countriesState.requestAllCountries();
    this.mountDataToGoogle();
  }

  TogglePopup(){
    this.buttonTrigger = false;
  }

  onDateSelected() {
    if (!this.selectedDate) {
      return;
    }

    if (moment(this.selectedDate).isValid()) {
      if (this.profile) {
        this.profile.birthdate = moment(this.selectedDate).tz(this.timezone).format('YYYY-MM-DD');
        return moment(this.selectedDate).tz(this.timezone).format('dddd, MMMM Do YYYY');
      }
    }
  }

  @Watch('profile', { immediate: true, deep: true })
  onWatchProfile(v: UserProfileModel) {
    if (!!v) {
      this.model.user_profile = {
        ...this.model.user_profile,
        ...v
      };
    }
  }

  @Watch('contestant', { deep: true, immediate: true })
  onWatchContestant(v: ProfileContestantModel) {
    if (!!v) {
      this.model = {
        ...this.model,
        ...v,
        longitude: Number(v.longitude),
        latitude: Number(v.latitude)
      };
    }
  }

  async onSubmit() {
    if (this.profileForm.validate() && !!this.googleLocation) {
      this.submitting = true;

      this.model.location = this.googleLocation.description;
      this.model.latitude = this.googleLocation.latitude;
      this.model.longitude = this.googleLocation.longitude;

      try {
        await userState.updateUser();
        await userState.setProfile(this.model);
        // this.snackbarVisibility = true;
        // this.snackbarColor = 'green';
        // this.snackbarText = 'profile.messages.update_success';
        this.buttonTrigger = true;
        window.scrollTo(0, 0);
      } catch (error) {
        this.snackbarVisibility = true;
        this.snackbarColor = 'red';
        this.snackbarText = 'profile.messages.update_failed';
      } finally {
        this.submitting = false;
      }
    } else {
      this.snackbarVisibility = true;
      this.snackbarColor = 'red';
      this.snackbarText = 'profile.messages.update_failed';
      this.scrollToErrors();
    }
  }

  @Emit()
  onNextStep() {
    return;
  }

  onClickToOpen() {
    // @ts-ignore
    this.fileUpload.onOpenFileChooser(true);
  }

  onUploadStart(e: any) {
    this.uploading = true;
  }

  onUploadFinish({ target }: ProgressEvent) {
    this.uploading = false;
    if (this.user && target) {
      // @ts-ignore
      const { avatar } = JSON.parse(target.response);
      this.user.avatar = avatar;
    }
  }

  onLocationChange(data: GoogleLocationSelected) {
    this.googleLocation = data;
  }

  mountDataToGoogle() {
    setTimeout(() => {
      const { longitude, latitude, location } = this.model;
      if (!longitude || !latitude || !location) {
        return null;
      }
      this.googleLocation = { longitude, latitude, description: location };
    }, 2000);
  }

  onUploadFailed(e: any) {
    this.uploading = false;
  }

  private scrollToErrors() {
    let target: any = null;
    if (!this.user.first_name || !this.user.last_name) {
      target = this.$refs.fullNameField;
    } else if (!this.model?.user_profile?.about) {
      target = this.$refs.aboutField;
    } else if (!this.model.nationality) {
      target = this.$refs.nationField;
    } else if (!this.model.country_residence) {
      target = this.$refs.residenceField;
    } else if (!this.googleLocation) {
      target = this.$refs.locationField;
    } else if (!this.model?.user_profile?.birthdate) {
      target = this.$refs.birthDateField;
    } else if (!this.model?.user_profile?.gender) {
      target = this.$refs.genderField;
    }
    if (target) {
      this.$vuetify.goTo(target, { offset: 20 });
    }
  }
}
